import React, { useState, useEffect, useCallback } from 'react';
import { Flag, CheckCircle, XCircle, Clock, Eye, Trash2, RefreshCw, Globe, FileQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/config';

// أنواع أخطاء الأسئلة
const questionReportTypes = {
  wrong_answer: { ar: 'الإجابة الصحيحة خاطئة', sv: 'Fel svar', en: 'Wrong answer' },
  translation_error: { ar: 'خطأ في الترجمة', sv: 'Översättningsfel', en: 'Translation error' },
  missing_image: { ar: 'صورة مفقودة', sv: 'Bild saknas', en: 'Missing image' },
  unclear_question: { ar: 'السؤال غير واضح', sv: 'Otydlig fråga', en: 'Unclear question' },
  other: { ar: 'خطأ آخر', sv: 'Annat fel', en: 'Other error' }
};

// أنواع تقارير الموقع
const siteReportTypes = {
  translation_error: { ar: 'خطأ في الترجمة', sv: 'Översättningsfel', en: 'Translation error' },
  broken_feature: { ar: 'ميزة لا تعمل', sv: 'Funktion fungerar inte', en: 'Feature not working' },
  display_issue: { ar: 'مشكلة في العرض', sv: 'Visningsproblem', en: 'Display issue' },
  missing_content: { ar: 'محتوى مفقود', sv: 'Saknat innehåll', en: 'Missing content' },
  suggestion: { ar: 'اقتراح تحسين', sv: 'Förbättringsförslag', en: 'Improvement suggestion' },
  other: { ar: 'أخرى', sv: 'Annat', en: 'Other' }
};

// حالات التقرير
const statusConfig = {
  pending: { icon: Clock, color: 'text-amber-600 bg-amber-100', label: { ar: 'قيد الانتظار', sv: 'Väntar', en: 'Pending' } },
  reviewed: { icon: Eye, color: 'text-blue-600 bg-blue-100', label: { ar: 'تمت المراجعة', sv: 'Granskad', en: 'Reviewed' } },
  fixed: { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-100', label: { ar: 'تم الإصلاح', sv: 'Fixad', en: 'Fixed' } },
  rejected: { icon: XCircle, color: 'text-red-600 bg-red-100', label: { ar: 'مرفوض', sv: 'Avvisad', en: 'Rejected' } }
};

const QuestionReports = ({ token, i18n }) => {
  const [questionReports, setQuestionReports] = useState([]);
  const [siteReports, setSiteReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('questions'); // questions or site
  const [refreshing, setRefreshing] = useState(false);
  const lang = i18n?.language || 'ar';

  const fetchReports = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch question reports
      const qUrl = filterStatus === 'all' 
        ? `${API}/admin/question-reports`
        : `${API}/admin/question-reports?status=${filterStatus}`;
      const qRes = await axios.get(qUrl, { headers });
      setQuestionReports(qRes.data || []);
      
      // Fetch site reports
      const sUrl = filterStatus === 'all' 
        ? `${API}/admin/site-reports`
        : `${API}/admin/site-reports?status=${filterStatus}`;
      const sRes = await axios.get(sUrl, { headers });
      setSiteReports(sRes.data || []);
      
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error(lang === 'ar' ? 'خطأ في جلب التقارير' : 'Error fetching reports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, filterStatus, lang]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReports();
    toast.success(lang === 'ar' ? 'تم تحديث التقارير' : 'Reports refreshed');
  };

  const updateStatus = async (reportId, newStatus, isQuestionReport = true) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = isQuestionReport 
        ? `${API}/admin/question-reports/${reportId}?status=${newStatus}`
        : `${API}/admin/site-reports/${reportId}?status=${newStatus}`;
      
      await axios.put(endpoint, {}, { headers });
      
      // تحديث القائمة
      if (isQuestionReport) {
        setQuestionReports(prev => prev.map(r => 
          r.id === reportId ? { ...r, status: newStatus } : r
        ));
      } else {
        setSiteReports(prev => prev.map(r => 
          r.id === reportId ? { ...r, status: newStatus } : r
        ));
      }
      
      toast.success(lang === 'ar' ? 'تم تحديث الحالة' : 'Status updated');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(lang === 'ar' ? 'خطأ في تحديث الحالة' : 'Error updating status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString(lang === 'ar' ? 'ar-SA' : 'sv-SE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  const reports = activeTab === 'questions' ? questionReports : siteReports;
  const reportTypes = activeTab === 'questions' ? questionReportTypes : siteReportTypes;
  const pendingCount = reports.filter(r => r.status === 'pending').length;
  const totalPending = questionReports.filter(r => r.status === 'pending').length + siteReports.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Flag className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">{lang === 'ar' ? 'إجمالي التقارير' : 'Total Reports'}</p>
              <p className="text-2xl font-bold text-slate-900">{questionReports.length + siteReports.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">{lang === 'ar' ? 'قيد الانتظار' : 'Pending'}</p>
              <p className="text-2xl font-bold text-amber-600">{totalPending}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileQuestion className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">{lang === 'ar' ? 'تقارير الأسئلة' : 'Question Reports'}</p>
              <p className="text-2xl font-bold text-blue-600">{questionReports.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">{lang === 'ar' ? 'تقارير الموقع' : 'Site Reports'}</p>
              <p className="text-2xl font-bold text-purple-600">{siteReports.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Flag className="w-7 h-7 text-orange-600" />
            {lang === 'ar' ? 'تقارير الأخطاء' : 'Error Reports'}
          </h3>
          <div className="flex items-center gap-3">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === 'ar' ? 'الكل' : 'All'}</SelectItem>
                <SelectItem value="pending">{lang === 'ar' ? 'قيد الانتظار' : 'Pending'}</SelectItem>
                <SelectItem value="reviewed">{lang === 'ar' ? 'تمت المراجعة' : 'Reviewed'}</SelectItem>
                <SelectItem value="fixed">{lang === 'ar' ? 'تم الإصلاح' : 'Fixed'}</SelectItem>
                <SelectItem value="rejected">{lang === 'ar' ? 'مرفوض' : 'Rejected'}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleRefresh} variant="outline" size="sm" disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('questions')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'questions' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <FileQuestion className="w-4 h-4" />
            {lang === 'ar' ? 'تقارير الأسئلة' : 'Question Reports'}
            {questionReports.filter(r => r.status === 'pending').length > 0 && (
              <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {questionReports.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('site')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'site' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Globe className="w-4 h-4" />
            {lang === 'ar' ? 'تقارير الموقع' : 'Site Reports'}
            {siteReports.filter(r => r.status === 'pending').length > 0 && (
              <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {siteReports.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          {reports.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Flag className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{lang === 'ar' ? 'لا توجد تقارير' : 'No reports found'}</p>
            </div>
          ) : (
            reports.map((report) => {
              const statusConf = statusConfig[report.status] || statusConfig.pending;
              const StatusIcon = statusConf.icon;
              
              return (
                <div key={report.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* نوع الخطأ والحالة */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusConf.color}`}>
                          <StatusIcon className="w-3 h-3 inline me-1" />
                          {statusConf.label[lang] || statusConf.label.en}
                        </span>
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs font-medium">
                          {reportTypes[report.report_type]?.[lang] || report.report_type}
                        </span>
                      </div>
                      
                      {/* نص السؤال أو رابط الصفحة */}
                      <p className="text-slate-900 font-medium mb-2 line-clamp-2">
                        {activeTab === 'questions' 
                          ? (report.question_text || 'Question ID: ' + report.question_id)
                          : (report.page_url || 'No URL')
                        }
                      </p>
                      
                      {/* التفاصيل */}
                      {report.details && (
                        <p className="text-slate-600 text-sm mb-2">
                          <span className="font-medium">{lang === 'ar' ? 'التفاصيل:' : 'Details:'}</span> {report.details}
                        </p>
                      )}
                      
                      {/* معلومات المرسل */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>{report.user_name} ({report.user_email})</span>
                        <span>{formatDate(report.created_at)}</span>
                      </div>
                    </div>
                    
                    {/* أزرار التحكم */}
                    <div className="flex flex-col gap-2">
                      {report.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => updateStatus(report.id, 'reviewed', activeTab === 'questions')}
                          >
                            <Eye className="w-4 h-4 me-1" />
                            {lang === 'ar' ? 'مراجعة' : 'Review'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => updateStatus(report.id, 'fixed', activeTab === 'questions')}
                          >
                            <CheckCircle className="w-4 h-4 me-1" />
                            {lang === 'ar' ? 'تم الإصلاح' : 'Fixed'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => updateStatus(report.id, 'rejected', activeTab === 'questions')}
                          >
                            <XCircle className="w-4 h-4 me-1" />
                            {lang === 'ar' ? 'رفض' : 'Reject'}
                          </Button>
                        </>
                      )}
                      {report.status === 'reviewed' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => updateStatus(report.id, 'fixed', activeTab === 'questions')}
                          >
                            <CheckCircle className="w-4 h-4 me-1" />
                            {lang === 'ar' ? 'تم الإصلاح' : 'Fixed'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => updateStatus(report.id, 'rejected', activeTab === 'questions')}
                          >
                            <XCircle className="w-4 h-4 me-1" />
                            {lang === 'ar' ? 'رفض' : 'Reject'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionReports;
